/**
 * Utilities for managing editor history snapshots
 */

import { Block } from '@/components/editor/Editor';

/**
 * Represents a snapshot of the editor state at a point in time
 */
export interface EditorSnapshot {
  blocks: Block[];           // Complete block array (deep copy)
  activeBlockId: string;     // Which block had focus
  cursorPosition: number;    // Character offset in active block
  timestamp: number;         // For debouncing logic
}

/**
 * Create a deep copy of blocks to prevent mutations affecting history
 */
export const cloneBlocks = (blocks: Block[]): Block[] => {
  return blocks.map(block => ({
    ...block,
    metadata: block.metadata ? { ...block.metadata } : undefined,
  }));
};

/**
 * Create a snapshot of the current editor state
 */
export const createSnapshot = (
  blocks: Block[],
  activeBlockId: string,
  cursorPosition: number
): EditorSnapshot => {
  return {
    blocks: cloneBlocks(blocks),
    activeBlockId,
    cursorPosition,
    timestamp: Date.now(),
  };
};

/**
 * Compare two snapshots to determine if they are meaningfully different
 * Returns true if snapshots are different enough to warrant saving
 */
export const areSnapshotsDifferent = (
  snapshot1: EditorSnapshot | null,
  snapshot2: EditorSnapshot
): boolean => {
  if (!snapshot1) return true;

  // Check if blocks are different
  if (snapshot1.blocks.length !== snapshot2.blocks.length) return true;

  // Check if any block content, type, or metadata has changed
  for (let i = 0; i < snapshot1.blocks.length; i++) {
    const block1 = snapshot1.blocks[i];
    const block2 = snapshot2.blocks[i];

    if (
      block1.id !== block2.id ||
      block1.type !== block2.type ||
      block1.content !== block2.content ||
      JSON.stringify(block1.metadata) !== JSON.stringify(block2.metadata)
    ) {
      return true;
    }
  }

  // Check if active block or cursor position changed significantly
  // We don't consider just cursor movement as a change unless content changed
  if (snapshot1.activeBlockId !== snapshot2.activeBlockId) {
    return true;
  }

  return false;
};

/**
 * Check if a snapshot represents a structural change (not just text edit)
 * Structural changes include: block addition/deletion/reordering/type change
 */
export const isStructuralChange = (
  previousSnapshot: EditorSnapshot | null,
  currentSnapshot: EditorSnapshot
): boolean => {
  if (!previousSnapshot) return true;

  // Check block count change
  if (previousSnapshot.blocks.length !== currentSnapshot.blocks.length) {
    return true;
  }

  // Check block ID order or type changes
  for (let i = 0; i < previousSnapshot.blocks.length; i++) {
    if (
      previousSnapshot.blocks[i].id !== currentSnapshot.blocks[i].id ||
      previousSnapshot.blocks[i].type !== currentSnapshot.blocks[i].type
    ) {
      return true;
    }
  }

  return false;
};
